import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { 
  Shield, Users, BarChart3, Server, Ban, UserCheck, Trash2, Loader2, Check, 
  AlertTriangle, RefreshCw, Plus, Edit2, X, Save, ArrowLeft, Bell, MessageSquare,
  HelpCircle, Lightbulb, Eye, Send,
  // Model icons
  Brain, Wand2, Scale, HeartPulse, Code2, Palette, TrendingUp, LineChart,
  GraduationCap, Briefcase, Globe, Wrench, Bot
} from 'lucide-react';

// Icon mapping for models
const MODEL_ICONS = {
  sage: Brain,
  general: Brain,
  prompting: Wand2,
  legal: Scale,
  medical: HeartPulse,
  coding: Code2,
  code: Code2,
  creative: Palette,
  reseller: TrendingUp,
  resale: TrendingUp,
  finance: LineChart,
  education: GraduationCap,
  business: Briefcase,
  travel: Globe,
  ideas: Lightbulb,
  technical: Wrench,
  security: Shield,
  default: Bot
};

// Get icon component for a model
const getModelIcon = (model) => {
  if (!model) return Bot;
  const id = (model.id || '').toLowerCase();
  const name = (model.name || '').toLowerCase();
  
  for (const [key, IconComponent] of Object.entries(MODEL_ICONS)) {
    if (id.includes(key) || name.includes(key)) {
      return IconComponent;
    }
  }
  return Bot;
};

// Styled icon component with glow
const ModelIcon = ({ model, size = 'md' }) => {
  const IconComponent = getModelIcon(model);
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };
  
  return (
    <div className="relative flex items-center justify-center">
      <div className="absolute inset-0 bg-[#00d4ff]/20 blur-md rounded-full" />
      <IconComponent 
        className={`${sizeClasses[size]} text-[#00d4ff] relative z-10`}
        style={{ filter: 'drop-shadow(0 0 4px rgba(0, 212, 255, 0.6))' }}
      />
    </div>
  );
};

// Default AI models that come pre-configured
const DEFAULT_MODELS = [
  { id: 'sage', name: 'Sage', description: 'Your intelligent all-purpose AI assistant.', system_prompt: 'You are Sage, a helpful and knowledgeable AI assistant. Be concise, accurate, and friendly.', is_default: true },
  { id: 'prompting', name: 'Prompting Expert', description: 'Specializes in crafting effective prompts for AI systems.', system_prompt: 'You are Sage in Prompting Expert mode. You specialize in helping users craft effective prompts for AI systems. Guide users in writing clear, specific, and effective prompts. Explain prompt engineering techniques like chain-of-thought, few-shot learning, and role-playing.', is_default: true },
  { id: 'legal', name: 'Legal Advisor', description: 'Provides general legal information and guidance (not legal advice).', system_prompt: 'You are Sage in Legal Advisor mode. You provide general legal information and guidance. Always remind users that you are not a licensed attorney and cannot provide legal advice. Encourage users to consult with a qualified legal professional for specific legal matters. Be thorough in explaining legal concepts.', is_default: true },
  { id: 'medical', name: 'Medical Guide', description: 'Offers health information and wellness guidance (not medical advice).', system_prompt: 'You are Sage in Medical Guide mode. You provide general health information and wellness guidance. Always remind users that you are not a licensed medical professional and cannot provide medical advice. Encourage users to consult with healthcare providers for medical concerns. Be empathetic and informative.', is_default: true },
  { id: 'coding', name: 'Code Assistant', description: 'Expert programmer helping with code, debugging, and technical questions.', system_prompt: 'You are Sage in Code Assistant mode. You are an expert programmer proficient in multiple languages. Help users write clean, efficient code. Explain concepts clearly, provide examples, and suggest best practices. Debug issues methodically.', is_default: true },
  { id: 'creative', name: 'Creative Writer', description: 'Assists with creative writing, storytelling, and content creation.', system_prompt: 'You are Sage in Creative Writer mode. You excel at creative writing, storytelling, and content creation. Help users with brainstorming ideas, developing narratives, writing compelling content, and improving their creative work. Be imaginative and inspiring.', is_default: true }
];

export default function AdminPanel({ navigate }) {
  const { user, isAdmin } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState({ llm: null, supabase: null });
  const [search, setSearch] = useState('');
  
  // AI Models state
  const [models, setModels] = useState([]);
  const [editingModel, setEditingModel] = useState(null);
  const [showModelForm, setShowModelForm] = useState(false);
  const [modelForm, setModelForm] = useState({ name: '', description: '', system_prompt: '' });
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [responseText, setResponseText] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('home');
      return;
    }
    
    if (tab === 'users') fetchUsers();
    else if (tab === 'stats') fetchStats();
    else if (tab === 'models') fetchModels();
    else if (tab === 'notifications') fetchNotifications();
  }, [tab, isAdmin]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [usersRes, messagesRes, postsRes] = await Promise.all([
        supabase.from('users').select('*', { count: 'exact', head: true }),
        supabase.from('messages').select('*', { count: 'exact', head: true }),
        supabase.from('social_posts').select('*', { count: 'exact', head: true })
      ]);

      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const { count: newUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', weekAgo.toISOString());

      const dayAgo = new Date();
      dayAgo.setDate(dayAgo.getDate() - 1);
      const { count: recentMessages } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', dayAgo.toISOString());

      setStats({
        totalUsers: usersRes.count || 0,
        totalMessages: messagesRes.count || 0,
        totalPosts: postsRes.count || 0,
        newUsers: newUsers || 0,
        recentMessages: recentMessages || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleBan = async (userId, isBanned) => {
    if (userId === user.id) return;
    try {
      await supabase.from('users').update({ is_banned: !isBanned }).eq('id', userId);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_banned: !isBanned } : u));
    } catch (error) {
      console.error('Error toggling ban:', error);
    }
  };

  const toggleAdmin = async (userId, isUserAdmin) => {
    if (userId === user.id) return;
    try {
      await supabase.from('users').update({ is_admin: !isUserAdmin }).eq('id', userId);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_admin: !isUserAdmin } : u));
    } catch (error) {
      console.error('Error toggling admin:', error);
    }
  };

  const deleteUser = async (userId) => {
    if (userId === user.id) return;
    if (!window.confirm('Delete this user?')) return;
    
    try {
      await supabase.from('users').delete().eq('id', userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const testLLM = async () => {
    setSystemStatus(prev => ({ ...prev, llm: 'testing' }));
    try {
      const start = Date.now();
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: [{ role: 'user', content: 'ping' }] })
      });
      const latency = Date.now() - start;
      setSystemStatus(prev => ({ ...prev, llm: response.ok ? { ok: true, latency } : { ok: false } }));
    } catch {
      setSystemStatus(prev => ({ ...prev, llm: { ok: false } }));
    }
  };

  const testSupabase = async () => {
    setSystemStatus(prev => ({ ...prev, supabase: 'testing' }));
    try {
      const start = Date.now();
      await supabase.from('users').select('id').limit(1);
      const latency = Date.now() - start;
      setSystemStatus(prev => ({ ...prev, supabase: { ok: true, latency } }));
    } catch {
      setSystemStatus(prev => ({ ...prev, supabase: { ok: false } }));
    }
  };

  const clearGeneralMessages = async () => {
    if (!window.confirm('Clear ALL general chat messages?')) return;
    try {
      await supabase.from('messages').delete().eq('room', 'general');
      alert('Messages cleared');
    } catch (error) {
      console.error('Error clearing messages:', error);
    }
  };

  // Notifications Functions
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        // Fallback - try to get from all users' localStorage (not practical, show empty)
        console.error('Error fetching notifications:', error);
        setNotifications([]);
      } else {
        setNotifications(data || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const updateSubmissionStatus = async (id, status) => {
    try {
      const { error } = await supabase
        .from('user_submissions')
        .update({ status })
        .eq('id', id);

      if (error) throw error;
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, status } : n));
      if (selectedNotification?.id === id) {
        setSelectedNotification(prev => ({ ...prev, status }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const sendAdminResponse = async (id) => {
    if (!responseText.trim()) return;
    try {
      const { error } = await supabase
        .from('user_submissions')
        .update({ admin_response: responseText.trim() })
        .eq('id', id);

      if (error) throw error;
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, admin_response: responseText.trim() } : n));
      if (selectedNotification?.id === id) {
        setSelectedNotification(prev => ({ ...prev, admin_response: responseText.trim() }));
      }
      setResponseText('');
    } catch (error) {
      console.error('Error sending response:', error);
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Delete this submission?')) return;
    try {
      await supabase.from('user_submissions').delete().eq('id', id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setSelectedNotification(null);
    } catch (error) {
      console.error('Error deleting submission:', error);
    }
  };

  const notificationStatuses = {
    pending: { label: 'Pending', color: '#ffcc00' },
    'in-progress': { label: 'In Progress', color: '#00d4ff' },
    completed: { label: 'Completed', color: '#00ff88' },
    declined: { label: 'Declined', color: '#ff3366' }
  };

  // AI Models Functions
  const fetchModels = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('ai_models')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching models from DB:', error.message);
        // If table doesn't exist yet, use defaults from localStorage
        const storedModels = JSON.parse(localStorage.getItem('sage_ai_models') || 'null');
        setModels(storedModels || DEFAULT_MODELS);
      } else if (!data || data.length === 0) {
        // No models in DB yet - seed with defaults
        console.log('No models in DB, using defaults');
        setModels(DEFAULT_MODELS);
      } else {
        // Merge database models with defaults (in case new defaults were added)
        const dbModelIds = data.map(m => m.id);
        const missingDefaults = DEFAULT_MODELS.filter(dm => !dbModelIds.includes(dm.id));
        
        if (missingDefaults.length > 0) {
          console.log('Found missing default models, adding to list:', missingDefaults.map(m => m.id));
        }
        
        setModels([...data, ...missingDefaults]);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      setModels(DEFAULT_MODELS);
    } finally {
      setLoading(false);
    }
  };

  const saveModel = async () => {
    if (!modelForm.name.trim() || !modelForm.system_prompt.trim()) return;

    setSaving(true);
    setSaveSuccess(false);
    
    const modelData = {
      name: modelForm.name.trim(),
      description: modelForm.description.trim(),
      system_prompt: modelForm.system_prompt.trim(),
      is_active: true
    };

    try {
      if (editingModel) {
        // First check if this model exists in the database
        const { data: existingModel, error: fetchError } = await supabase
          .from('ai_models')
          .select('id')
          .eq('id', editingModel.id)
          .single();

        if (fetchError || !existingModel) {
          // Model doesn't exist in DB (it's a default model being edited for the first time)
          // Insert it as a new record with the same ID
          console.log('Model not found in DB, inserting as new record:', editingModel.id);
          const { data: insertedModel, error: insertError } = await supabase
            .from('ai_models')
            .insert({ 
              id: editingModel.id,
              ...modelData, 
              is_default: editingModel.is_default || false 
            })
            .select()
            .single();

          if (insertError) {
            console.error('Insert error:', insertError);
            throw insertError;
          }
          
          setModels(prev => prev.map(m => m.id === editingModel.id ? { ...m, ...modelData, ...insertedModel } : m));
          console.log('Model inserted successfully');
        } else {
          // Model exists in DB, update it
          console.log('Updating existing model in DB:', editingModel.id);
          const { data: updatedModel, error: updateError } = await supabase
            .from('ai_models')
            .update(modelData)
            .eq('id', editingModel.id)
            .select()
            .single();

          if (updateError) {
            console.error('Update error:', updateError);
            throw updateError;
          }
          
          setModels(prev => prev.map(m => m.id === editingModel.id ? { ...m, ...modelData } : m));
          console.log('Model updated successfully');
        }
      } else {
        // Create new model - let Supabase generate the UUID
        const { data, error } = await supabase
          .from('ai_models')
          .insert({ ...modelData, is_default: false })
          .select()
          .single();

        if (error) {
          console.error('Insert new model error:', error);
          throw error;
        }
        
        setModels(prev => [...prev, data]);
        console.log('New model created successfully');
      }
      
      // Show success feedback
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      resetModelForm();
    } catch (error) {
      console.error('Error saving model:', error);
      alert('Failed to save model. Error: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteModel = async (modelId, isDefault) => {
    if (isDefault) {
      alert('Cannot delete default models');
      return;
    }
    if (!window.confirm('Delete this model?')) return;

    try {
      await supabase.from('ai_models').delete().eq('id', modelId);
      setModels(prev => prev.filter(m => m.id !== modelId));
    } catch (error) {
      console.error('Error deleting model:', error);
      setModels(prev => prev.filter(m => m.id !== modelId));
      localStorage.setItem('sage_ai_models', JSON.stringify(models.filter(m => m.id !== modelId)));
    }
  };

  const toggleModelActive = async (modelId, currentStatus) => {
    const newStatus = !currentStatus;
    try {
      // First check if model exists in DB
      const { data: existingModel, error: fetchError } = await supabase
        .from('ai_models')
        .select('id')
        .eq('id', modelId)
        .single();

      if (fetchError || !existingModel) {
        // Model doesn't exist in DB, need to insert it first
        const model = models.find(m => m.id === modelId);
        if (model) {
          await supabase.from('ai_models').insert({
            id: modelId,
            name: model.name,
            description: model.description || '',
            system_prompt: model.system_prompt,
            is_active: newStatus,
            is_default: model.is_default || false
          });
        }
      } else {
        await supabase.from('ai_models').update({ is_active: newStatus }).eq('id', modelId);
      }
      
      setModels(prev => prev.map(m => m.id === modelId ? { ...m, is_active: newStatus } : m));
    } catch (error) {
      console.error('Error toggling model active:', error);
      // Still update local state for UI responsiveness
      setModels(prev => prev.map(m => m.id === modelId ? { ...m, is_active: newStatus } : m));
    }
  };

  const editModel = (model) => {
    setEditingModel(model);
    setModelForm({
      name: model.name,
      description: model.description || '',
      system_prompt: model.system_prompt
    });
    setShowModelForm(true);
  };

  const resetModelForm = () => {
    setModelForm({ name: '', description: '', system_prompt: '' });
    setEditingModel(null);
    setShowModelForm(false);
  };

  const filteredUsers = users.filter(u => 
    u.username?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#08080c] flex items-center justify-center pb-20">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-[#ff3366] mx-auto mb-4" />
          <p className="text-[#ff3366] font-mono text-xs uppercase tracking-wider">Access Denied</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'models', label: 'Models', icon: Brain },
    { id: 'notifications', label: 'Notifications', icon: Bell, badge: notifications.filter(n => n.status === 'pending').length },
    { id: 'stats', label: 'Stats', icon: BarChart3 },
    { id: 'system', label: 'System', icon: Server }
  ];

  return (
    <div className="min-h-screen bg-[#08080c] pb-20" data-testid="admin-panel">
      {/* Header */}
      <div className="p-4 border-b border-[#1a1a24]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-4 h-4 text-[#00d4ff]" />
            <h1 className="font-mono text-xs text-[#00d4ff] uppercase tracking-[0.2em]">Admin Panel</h1>
          </div>
          <button
            onClick={() => navigate('home')}
            className="flex items-center gap-2 px-3 py-2 border border-[#1a1a24] text-[#7a7a9a] hover:text-[#e8e8f0] hover:border-[#00d4ff]/30 transition-colors"
            data-testid="admin-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="font-mono text-[10px] uppercase">Exit</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#1a1a24]">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 py-3 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-wider transition-all relative ${
              tab === t.id
                ? 'text-[#00d4ff] border-b border-[#00d4ff] bg-[#00d4ff]/5'
                : 'text-[#4a4a60] hover:text-[#7a7a9a]'
            }`}
            data-testid={`admin-tab-${t.id}`}
          >
            <t.icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t.label}</span>
            {t.badge > 0 && (
              <span className="absolute -top-1 right-2 sm:static sm:ml-1 bg-[#ff3366] text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-5 h-5 text-[#00d4ff] animate-spin" />
        </div>
      ) : (
        <div className="p-4">
          {/* Users Tab */}
          {tab === 'users' && (
            <div data-testid="admin-users">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full bg-[#0d0d12] border border-[#1a1a24] text-[#e8e8f0] px-3 py-2.5 text-xs font-mono mb-4 focus:border-[#00d4ff]/50 outline-none"
                data-testid="user-search"
              />
              <div className="space-y-2">
                {filteredUsers.map((u) => (
                  <div
                    key={u.id}
                    className="bg-[#0d0d12] border border-[#1a1a24] p-3 hover:border-[#00d4ff]/20 transition-colors"
                    data-testid={`admin-user-${u.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-mono text-xs text-[#e8e8f0]">{u.username}</p>
                          {u.is_admin && (
                            <span className="text-[8px] border border-[#00d4ff]/30 text-[#00d4ff] px-1.5 py-0.5 uppercase tracking-wider">Admin</span>
                          )}
                          {u.is_banned && (
                            <span className="text-[8px] border border-[#ff3366]/30 text-[#ff3366] px-1.5 py-0.5 uppercase tracking-wider">Banned</span>
                          )}
                        </div>
                        <p className="text-[10px] text-[#4a4a60] truncate mt-0.5">{u.email}</p>
                        <p className="text-[9px] text-[#4a4a60] mt-0.5 font-mono">
                          Joined {new Date(u.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {u.id !== user.id && (
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            onClick={() => toggleBan(u.id, u.is_banned)}
                            className={`p-2 border transition-colors ${
                              u.is_banned 
                                ? 'border-[#00ff88]/30 text-[#00ff88] hover:bg-[#00ff88]/10' 
                                : 'border-[#ff3366]/30 text-[#ff3366] hover:bg-[#ff3366]/10'
                            }`}
                            title={u.is_banned ? 'Unban' : 'Ban'}
                            data-testid={`ban-user-${u.id}`}
                          >
                            {u.is_banned ? <UserCheck className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            onClick={() => toggleAdmin(u.id, u.is_admin)}
                            className={`p-2 border transition-colors ${
                              u.is_admin 
                                ? 'border-[#ffcc00]/30 text-[#ffcc00] hover:bg-[#ffcc00]/10' 
                                : 'border-[#00d4ff]/30 text-[#00d4ff] hover:bg-[#00d4ff]/10'
                            }`}
                            title={u.is_admin ? 'Demote' : 'Promote'}
                            data-testid={`admin-toggle-${u.id}`}
                          >
                            <Shield className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => deleteUser(u.id)}
                            className="p-2 border border-[#ff3366]/30 text-[#ff3366] hover:bg-[#ff3366]/10 transition-colors"
                            title="Delete"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Models Tab */}
          {tab === 'models' && (
            <div data-testid="admin-models">
              {/* Success Banner */}
              {saveSuccess && !showModelForm && (
                <div className="bg-[#00ff88]/10 border border-[#00ff88]/30 p-3 mb-4 flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#00ff88]" />
                  <span className="font-mono text-xs text-[#00ff88]">Model saved successfully!</span>
                </div>
              )}
              
              {/* Add Model Form */}
              {showModelForm && (
                <div className="bg-[#0d0d12] border border-[#00d4ff]/30 p-4 mb-4" data-testid="model-form">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Brain className="w-4 h-4 text-[#00d4ff]" />
                      <span className="font-mono text-xs text-[#00d4ff] uppercase tracking-wider">
                        {editingModel ? 'Edit Model' : 'New Model'}
                      </span>
                    </div>
                    <button onClick={resetModelForm} className="p-1 hover:bg-[#111118]">
                      <X className="w-4 h-4 text-[#4a4a60]" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <input
                      type="text"
                      value={modelForm.name}
                      onChange={(e) => setModelForm({ ...modelForm, name: e.target.value })}
                      placeholder="Model name (e.g., Financial Advisor)"
                      className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] px-3 py-2 text-xs font-mono focus:border-[#00d4ff]/50 outline-none"
                      data-testid="model-name-input"
                    />

                    <input
                      type="text"
                      value={modelForm.description}
                      onChange={(e) => setModelForm({ ...modelForm, description: e.target.value })}
                      placeholder="Brief description for users"
                      className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] px-3 py-2 text-xs font-mono focus:border-[#00d4ff]/50 outline-none"
                      data-testid="model-description-input"
                    />

                    <textarea
                      value={modelForm.system_prompt}
                      onChange={(e) => setModelForm({ ...modelForm, system_prompt: e.target.value })}
                      placeholder="System prompt that defines this model's behavior, expertise, and personality..."
                      className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] px-3 py-2 text-xs font-mono focus:border-[#00d4ff]/50 outline-none resize-none"
                      rows={5}
                      data-testid="model-prompt-input"
                    />

                    <div className="flex gap-2">
                      <button
                        onClick={resetModelForm}
                        disabled={saving}
                        className="flex-1 border border-[#1a1a24] text-[#4a4a60] font-mono text-[10px] uppercase py-2 hover:bg-[#111118] disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveModel}
                        disabled={!modelForm.name.trim() || !modelForm.system_prompt.trim() || saving}
                        className={`flex-1 border font-mono text-[10px] uppercase py-2 flex items-center justify-center gap-2 transition-all ${
                          saveSuccess 
                            ? 'border-[#00ff88] text-[#00ff88] bg-[#00ff88]/10' 
                            : 'border-[#00d4ff] text-[#00d4ff] hover:bg-[#00d4ff]/10'
                        } disabled:opacity-30`}
                        data-testid="save-model-btn"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Saving...
                          </>
                        ) : saveSuccess ? (
                          <>
                            <Check className="w-3 h-3" />
                            Saved!
                          </>
                        ) : (
                          <>
                            <Save className="w-3 h-3" />
                            Save Model
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Add Model Button */}
              {!showModelForm && (
                <button
                  onClick={() => setShowModelForm(true)}
                  className="w-full border border-dashed border-[#1a1a24] text-[#4a4a60] font-mono text-[10px] uppercase py-3 hover:border-[#00d4ff]/50 hover:text-[#00d4ff] transition-colors mb-4 flex items-center justify-center gap-2"
                  data-testid="add-model-btn"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add New Model
                </button>
              )}

              {/* Models List */}
              <div className="space-y-2">
                {models.map((model) => (
                  <div
                    key={model.id}
                    className={`bg-[#0d0d12] border p-3 transition-colors ${
                      model.is_active !== false ? 'border-[#1a1a24] hover:border-[#00d4ff]/20' : 'border-[#1a1a24]/50 opacity-50'
                    }`}
                    data-testid={`admin-model-${model.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 border border-[#00d4ff]/20 rounded-lg flex items-center justify-center bg-[#00d4ff]/5 flex-shrink-0">
                          <ModelIcon model={model} size="md" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-mono text-xs text-[#00d4ff]">{model.name}</p>
                            {model.is_default && (
                              <span className="text-[8px] border border-[#4a4a60]/30 text-[#4a4a60] px-1.5 py-0.5 uppercase tracking-wider">Default</span>
                            )}
                            {model.is_active === false && (
                              <span className="text-[8px] border border-[#ff3366]/30 text-[#ff3366] px-1.5 py-0.5 uppercase tracking-wider">Disabled</span>
                            )}
                          </div>
                          <p className="text-[10px] text-[#4a4a60] mt-1 line-clamp-2">{model.description}</p>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={() => editModel(model)}
                          className="p-2 border border-[#00d4ff]/30 text-[#00d4ff] hover:bg-[#00d4ff]/10 transition-colors"
                          title="Edit"
                          data-testid={`edit-model-${model.id}`}
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        {!model.is_default && (
                          <button
                            onClick={() => deleteModel(model.id, model.is_default)}
                            className="p-2 border border-[#ff3366]/30 text-[#ff3366] hover:bg-[#ff3366]/10 transition-colors"
                            title="Delete"
                            data-testid={`delete-model-${model.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-[9px] text-[#4a4a60] mt-4 font-mono text-center">
                Users can select these models in the AI Chat screen
              </p>
            </div>
          )}

          {/* Notifications Tab */}
          {tab === 'notifications' && (
            <div data-testid="admin-notifications">
              {selectedNotification ? (
                <div className="bg-[#0d0d12] border border-[#1a1a24] p-4" data-testid="notification-detail">
                  <div className="flex items-center justify-between mb-4">
                    <button onClick={() => setSelectedNotification(null)} className="flex items-center gap-2 text-[#4a4a60] hover:text-[#00d4ff]">
                      <ArrowLeft className="w-4 h-4" />
                      <span className="font-mono text-[10px] uppercase">Back</span>
                    </button>
                    <button onClick={() => deleteSubmission(selectedNotification.id)} className="p-2 border border-[#ff3366]/30 text-[#ff3366] hover:bg-[#ff3366]/10">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  
                  <div className="flex items-start gap-3 mb-4">
                    {selectedNotification.type === 'support' ? (
                      <HelpCircle className="w-5 h-5 text-[#00d4ff] flex-shrink-0" />
                    ) : (
                      <Lightbulb className="w-5 h-5 text-[#ffcc00] flex-shrink-0" />
                    )}
                    <div>
                      <h3 className="font-mono text-sm text-[#e8e8f0] mb-1">{selectedNotification.subject}</h3>
                      <p className="text-[10px] text-[#4a4a60]">
                        From: {selectedNotification.username || 'Unknown'} • {new Date(selectedNotification.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-[#08080c] border border-[#1a1a24] p-3 rounded mb-4">
                    <p className="text-xs text-[#e8e8f0] whitespace-pre-wrap">{selectedNotification.description}</p>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-[9px] text-[#4a4a60] uppercase">Status:</span>
                    <div className="flex gap-1">
                      {Object.entries(notificationStatuses).map(([key, status]) => (
                        <button
                          key={key}
                          onClick={() => updateSubmissionStatus(selectedNotification.id, key)}
                          className={`px-2 py-1 text-[8px] font-mono uppercase rounded transition-all border ${
                            selectedNotification.status === key 
                              ? 'border-2' 
                              : 'border-[#1a1a24] hover:border-[#4a4a60]'
                          }`}
                          style={{ 
                            borderColor: selectedNotification.status === key ? status.color : undefined,
                            backgroundColor: selectedNotification.status === key ? `${status.color}15` : '#0d0d12',
                            color: selectedNotification.status === key ? status.color : '#7a7a9a'
                          }}
                        >
                          {status.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {selectedNotification.admin_response && (
                    <div className="bg-[#00d4ff]/5 border border-[#00d4ff]/30 p-3 rounded mb-4">
                      <p className="text-[9px] text-[#00d4ff] uppercase mb-1">Previous Response:</p>
                      <p className="text-xs text-[#e8e8f0]">{selectedNotification.admin_response}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-[9px] text-[#4a4a60] uppercase mb-2 block">Admin Response:</label>
                    <textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      placeholder="Write a response to the user..."
                      className="w-full bg-[#08080c] border border-[#1a1a24] text-[#e8e8f0] rounded px-3 py-2 text-xs resize-none focus:border-[#00d4ff]/50 outline-none mb-2"
                      rows={3}
                    />
                    <button
                      onClick={() => sendAdminResponse(selectedNotification.id)}
                      disabled={!responseText.trim()}
                      className="w-full bg-[#00d4ff]/20 border border-[#00d4ff] text-[#00d4ff] font-mono text-[10px] uppercase py-2 rounded hover:bg-[#00d4ff]/30 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Send className="w-3 h-3" />
                      Send Response
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-mono text-xs text-[#00d4ff] uppercase tracking-wider">User Submissions</h2>
                    <button onClick={fetchNotifications} className="p-2 border border-[#1a1a24] hover:border-[#00d4ff]/30 text-[#4a4a60]">
                      <RefreshCw className="w-3 h-3" />
                    </button>
                  </div>
                  
                  {notifications.length === 0 ? (
                    <div className="text-center py-12">
                      <Bell className="w-8 h-8 text-[#2a2a3a] mx-auto mb-3" />
                      <p className="text-[#4a4a60] text-xs font-mono">No submissions yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {notifications.map(notif => {
                        const status = notificationStatuses[notif.status] || notificationStatuses.pending;
                        return (
                          <button
                            key={notif.id}
                            onClick={() => setSelectedNotification(notif)}
                            className="w-full bg-[#0d0d12] border border-[#1a1a24] p-3 text-left hover:border-[#00d4ff]/30 transition-colors"
                            data-testid={`notification-${notif.id}`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2">
                                {notif.type === 'support' ? (
                                  <HelpCircle className="w-4 h-4 text-[#00d4ff]" />
                                ) : (
                                  <Lightbulb className="w-4 h-4 text-[#ffcc00]" />
                                )}
                                <span className="font-mono text-xs text-[#e8e8f0]">{notif.subject}</span>
                              </div>
                              <span
                                className="text-[8px] px-2 py-1 rounded uppercase font-mono"
                                style={{ backgroundColor: `${status.color}20`, color: status.color }}
                              >
                                {status.label}
                              </span>
                            </div>
                            <p className="text-[10px] text-[#7a7a9a] line-clamp-2 mb-2">{notif.description}</p>
                            <div className="flex items-center justify-between text-[9px] text-[#4a4a60]">
                              <span>{notif.username || 'Unknown'}</span>
                              <span>{new Date(notif.created_at).toLocaleDateString()}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Stats Tab */}
          {tab === 'stats' && (
            <div className="grid grid-cols-2 gap-3" data-testid="admin-stats">
              {[
                { label: 'Total Users', value: stats.totalUsers, color: '#e8e8f0' },
                { label: 'Total Messages', value: stats.totalMessages, color: '#e8e8f0' },
                { label: 'Total Posts', value: stats.totalPosts, color: '#e8e8f0' },
                { label: 'New Users (7d)', value: stats.newUsers, color: '#00ff88' }
              ].map((stat) => (
                <div key={stat.label} className="bg-[#0d0d12] border border-[#1a1a24] p-4">
                  <p className="font-mono text-[9px] text-[#4a4a60] uppercase tracking-wider mb-1">{stat.label}</p>
                  <p className="text-2xl font-light" style={{ color: stat.color }}>{stat.value}</p>
                </div>
              ))}
              <div className="col-span-2 bg-[#0d0d12] border border-[#1a1a24] p-4">
                <p className="font-mono text-[9px] text-[#4a4a60] uppercase tracking-wider mb-1">Messages (24h)</p>
                <p className="text-2xl font-light text-[#00d4ff]">{stats.recentMessages}</p>
              </div>
            </div>
          )}

          {/* System Tab */}
          {tab === 'system' && (
            <div className="space-y-3" data-testid="admin-system">
              <div className="bg-[#0d0d12] border border-[#1a1a24] p-4">
                <p className="font-mono text-[9px] text-[#4a4a60] uppercase tracking-wider mb-2">App Version</p>
                <p className="text-xs text-[#e8e8f0]">Sage App v1.0.0</p>
              </div>

              <div className="bg-[#0d0d12] border border-[#1a1a24] p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-mono text-[9px] text-[#4a4a60] uppercase tracking-wider">LLM Endpoint</p>
                  <button onClick={testLLM} className="text-[#00d4ff] p-1 hover:bg-[#00d4ff]/10 transition-colors" data-testid="test-llm-system">
                    <RefreshCw className={`w-3.5 h-3.5 ${systemStatus.llm === 'testing' ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                {systemStatus.llm && systemStatus.llm !== 'testing' && (
                  <div className={`flex items-center gap-2 ${systemStatus.llm.ok ? 'text-[#00ff88]' : 'text-[#ff3366]'}`}>
                    {systemStatus.llm.ok ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                    <span className="text-xs font-mono">
                      {systemStatus.llm.ok ? `Online (${systemStatus.llm.latency}ms)` : 'Offline'}
                    </span>
                  </div>
                )}
              </div>

              <div className="bg-[#0d0d12] border border-[#1a1a24] p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-mono text-[9px] text-[#4a4a60] uppercase tracking-wider">Supabase</p>
                  <button onClick={testSupabase} className="text-[#00d4ff] p-1 hover:bg-[#00d4ff]/10 transition-colors" data-testid="test-supabase-system">
                    <RefreshCw className={`w-3.5 h-3.5 ${systemStatus.supabase === 'testing' ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                {systemStatus.supabase && systemStatus.supabase !== 'testing' && (
                  <div className={`flex items-center gap-2 ${systemStatus.supabase.ok ? 'text-[#00ff88]' : 'text-[#ff3366]'}`}>
                    {systemStatus.supabase.ok ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                    <span className="text-xs font-mono">
                      {systemStatus.supabase.ok ? `Online (${systemStatus.supabase.latency}ms)` : 'Offline'}
                    </span>
                  </div>
                )}
              </div>

              <button
                onClick={clearGeneralMessages}
                className="w-full border border-[#ff3366]/30 text-[#ff3366] font-mono text-[10px] uppercase tracking-wider py-3 hover:bg-[#ff3366]/10 transition-colors"
                data-testid="clear-messages-btn"
              >
                Clear All General Messages
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
